import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { NavCountBadgeUrgent } from './sidebar/SidebarItem';
import { useAdminNotifications } from '../hooks/useAdminNotifications';

function formatNotifDate(value) {
  if (!value) return '';
  try {
    const d = new Date(value);
    if (!Number.isFinite(d.getTime())) return '';
    return d.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function isSafeInternalPath(path) {
  const p = String(path || '').trim();
  if (!p.startsWith('/') || p.startsWith('//')) return false;
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(p)) return false;
  if (/https?:/i.test(p)) return false;
  return (
    p.startsWith('/commercants/')
    || p === '/documents'
    || p.startsWith('/chat')
    || p.startsWith('/nr2j/')
  );
}

/**
 * Cloche Admin — commerçant (transverse) ou Super Admin / NR2J (plateforme).
 */
export default function AdminNotificationBell({ enabled = true, variant = 'merchant' }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const {
    items,
    unreadCount,
    loading,
    markRead,
    markAllRead,
    refresh,
  } = useAdminNotifications(enabled);

  useEffect(() => {
    if (!open) return undefined;
    refresh({ silent: true });
    const onDocClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, refresh]);

  if (!enabled) return null;

  const hasBadge = Number(unreadCount) > 0;

  const handleOpenToggle = () => {
    setOpen((prev) => !prev);
  };

  const handleItemClick = async (item) => {
    if (!item?.read) {
      await markRead(item.id);
    }
    setOpen(false);
    if (isSafeInternalPath(item?.actionPath)) {
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch {
        /* ignore */
      }
      navigate(item.actionPath);
    }
  };

  const panelTitle = variant === 'platform' ? 'Notifications plateforme' : 'Notifications';

  return (
    <div
      className="relative"
      ref={rootRef}
      data-testid={variant === 'platform' ? 'admin-notification-bell-platform' : 'admin-notification-bell'}
      data-bell-variant={variant}
    >
      <button
        type="button"
        onClick={handleOpenToggle}
        title={hasBadge ? `${unreadCount} notification(s) non lue(s)` : panelTitle}
        aria-label={hasBadge ? `${panelTitle} — ${unreadCount > 99 ? '99+' : unreadCount}` : panelTitle}
        aria-expanded={open}
        data-testid="admin-notification-bell-button"
        className="relative inline-flex h-10 w-10 items-center justify-center rounded-xl text-gray-600 hover:bg-gray-100 hover:text-gray-900"
      >
        <Bell className="h-5 w-5" aria-hidden="true" />
        {hasBadge ? (
          <span className="absolute -right-0.5 -top-0.5">
            <NavCountBadgeUrgent count={unreadCount} />
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          className="absolute right-0 z-50 mt-2 w-[min(100vw-2rem,22rem)] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg"
          data-testid="admin-notification-panel"
          role="dialog"
          aria-label={panelTitle}
        >
          <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-3 py-2.5">
            <p className="text-sm font-semibold text-gray-900">{panelTitle}</p>
            {hasBadge ? (
              <button
                type="button"
                onClick={() => markAllRead()}
                className="text-xs font-medium text-primary-700 hover:text-primary-900"
                data-testid="admin-notification-mark-all"
              >
                Tout marquer comme lu
              </button>
            ) : null}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {loading && items.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-gray-500">Chargement…</p>
            ) : null}
            {!loading && items.length === 0 ? (
              <p className="px-3 py-8 text-center text-sm text-gray-500" data-testid="admin-notification-empty">
                Aucune notification pour le moment
              </p>
            ) : null}
            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => handleItemClick(item)}
                className={`flex w-full flex-col gap-0.5 border-b border-gray-50 px-3 py-2.5 text-left hover:bg-gray-50 ${
                  item.read ? 'bg-white' : 'bg-amber-50/60'
                }`}
                data-testid="admin-notification-item"
              >
                <div className="flex items-start justify-between gap-2">
                  <span className={`text-sm ${item.read ? 'font-medium text-gray-800' : 'font-semibold text-gray-900'}`}>
                    {item.title}
                  </span>
                  {!item.read ? (
                    <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-amber-500" aria-hidden="true" />
                  ) : null}
                </div>
                {item.body ? (
                  <span className="line-clamp-2 text-xs text-gray-600">{item.body}</span>
                ) : null}
                <span className="text-[11px] text-gray-400">{formatNotifDate(item.createdAt)}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
